import { Button, Grid, Typography } from '@material-ui/core';
import React from 'react';

import { ECompanyType } from '~/src/dash/api/company';
import { IPublisher } from '~/src/dash/api/console-api';
import { CompanyMultiSelect } from '~/src/dash/components/dropdowns/company-multi-select';
import { FocusedTextfield } from '~/src/dash/components/focused-textfield';
import { VastEditor } from '~/src/dash/components/vast-editor';
import { IUserState } from '~/src/dash/reducers/user-reducers';
import { isPBSDomain } from '~/src/dash/resellers/utils/utils';
import { highlight, languages } from '~/src/prismjs';

// component note: this is used to popup and prompt the user to create a global mediafile or vast
// for a creative via name and url

interface IProps {
  newCreateName: string;
  newCreateURL: string;
  // handles change in name or url
  handleChange: (event: any) => void;
  // creates the asset/vast from this form
  handleCreateFromURL: () => void;
  handleUpdateFromURL?: () => void;
  buttonClass: any;
  isMediafile: boolean;
  publisher: IPublisher;
  edit?: boolean;
  handleChangeAdvertiser?: (advertiserIDs: number[]) => void;
  advertiserID?: number;
  user: IUserState;
}

export const CreateOrEditFromURLModal = (props: IProps) => {
  const {
    newCreateName,
    newCreateURL,
    handleChange,
    handleCreateFromURL,
    handleUpdateFromURL,
    handleChangeAdvertiser,
    buttonClass,
    isMediafile,
    publisher,
    advertiserID,
  } = props;
  return (
    <Grid item style={{ minWidth: 500, minHeight: 500, padding: 50, height: '100%' }}>
      <Grid
        container
        direction='column'
        justify='space-between'
        style={{ minWidth: 500, minHeight: 500, height: '100%' }}
      >
        <Grid item container direction='column' justify='flex-start' spacing={2}>
          {/* user prompt */}
          <Grid item style={{ width: '100%' }}>
            <Typography style={{ fontSize: 24, fontWeight: 500 }}>
              {isMediafile
                ? props.edit
                  ? 'Edit Asset'
                  : 'Create Asset from URL'
                : props.edit
                ? 'Edit VAST'
                : 'Create VAST from URL'}
            </Typography>
          </Grid>
          <Grid item style={{ width: '100%' }}>
            <Typography style={{ fontSize: 14, fontWeight: 400 }}>
              {isMediafile
                ? props.edit
                  ? ''
                  : 'Enter a valid URL for a .mp4 file to create an asset'
                : props.edit
                ? 'Enter a valid URL for a VAST to update'
                : 'Enter a valid URL for a VAST to create'}
            </Typography>
          </Grid>

          {/* Name */}
          <Grid item style={{ width: '100%' }}>
            <Grid container direction='row' alignItems='center' spacing={2}>
              <Grid item xs={2}>
                <Typography style={{ fontSize: 14, fontWeight: 400 }}>
                  {isMediafile ? 'Asset Name' : 'VAST Name'}
                </Typography>
              </Grid>
              <Grid item xs={8}>
                <FocusedTextfield
                  name={'newCreateName'}
                  fullWidth
                  multiline
                  value={newCreateName}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    handleChange(event);
                  }}
                />
              </Grid>
            </Grid>
          </Grid>

          {/* URL */}
          <Grid item style={{ width: '100%' }}>
            <Grid container direction='row' alignItems='flex-start' spacing={2}>
              <Grid item xs={2}>
                <Typography style={{ fontSize: 14, fontWeight: 400 }}>URL</Typography>
              </Grid>
              <Grid item xs={8}>
                {isMediafile ? (
                  <FocusedTextfield
                    name={'newCreateURL'}
                    fullWidth
                    multiline
                    value={newCreateURL}
                    disabled={props.edit}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                      handleChange(event);
                    }}
                  />
                ) : (
                  <VastEditor
                    url={newCreateURL}
                    urlID={0}
                    highlight={(code) => highlight(code, languages.elm, 'elm')}
                    onURLChange={(text: string) => {
                      const event = {
                        target: {
                          value: text,
                          name: 'newCreateURL',
                        },
                      };
                      handleChange(event);
                    }}
                    padding={3}
                    maxLength={2000}
                    textStyle={{
                      background: 'aliceblue',
                      borderRadius: 5,
                      opacity: 1,
                      padding: '5px 10px',
                    }}
                    wikiMsgHidden={publisher.Reseller && publisher.Reseller.ID === '4'}
                    disableSiteIDMacro={isPBSDomain(newCreateURL)}
                    edit={props.edit}
                  />
                )}
              </Grid>
            </Grid>
          </Grid>

          {/* Advertiser */}
          {handleChangeAdvertiser && (advertiserID || advertiserID === 0) && (
            <Grid item style={{ width: '100%' }}>
              <Grid container direction='row' alignItems='flex-start' spacing={2}>
                <Grid item xs={2}>
                  <Typography style={{ fontSize: 14, fontWeight: 400 }}>Advertiser</Typography>
                </Grid>
                <Grid item xs={8}>
                  <CompanyMultiSelect
                    onChange={handleChangeAdvertiser}
                    publisherID={publisher.ID}
                    companyIDs={[advertiserID]}
                    enableMultiSelect={false}
                    companyTypesAllowed={[
                      ECompanyType.TYPE_ADVERTISER,
                      ECompanyType.TYPE_AD_NETWORK,
                      ECompanyType.TYPE_HOUSE_ADVERTISER,
                    ]}
                    counter={0}
                    maxHeight={150}
                    username={props.user.user.Username}
                  />
                </Grid>
              </Grid>
            </Grid>
          )}
        </Grid>
        {/* create button */}
        <Grid item>
          <Button
            variant='contained'
            color='primary'
            component='span'
            onClick={props.edit ? handleUpdateFromURL : handleCreateFromURL}
            className={buttonClass}
          >
            <Typography noWrap style={{ textTransform: 'none', fontSize: '1rem' }}>
              {isMediafile
                ? props.edit
                  ? 'Update Asset'
                  : '+ Create Asset'
                : props.edit
                ? 'Update VAST'
                : '+ Create VAST'}
            </Typography>
          </Button>
        </Grid>
      </Grid>
    </Grid>
  );
};
